import type { SvgIconComponent } from "@mui/icons-material";
import CastForEducation from "@mui/icons-material/CastForEducation";
import MenuBook from "@mui/icons-material/MenuBook";
import School from "@mui/icons-material/School";
import type { SxProps, Theme, TooltipProps } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { NurseTrainingStatus } from "@/utils/models";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";

type IndicatorSpec = {
	Icon: SvgIconComponent;
	labelKey: string;
	testId: string;
};

// Only statuses that change how a shift is staffed get a marker. `Regular` is the norm and `Trainer`
// is an ordinary staffing contributor, so neither earns an icon — absence in this map means "render
// nothing", which is the intended outcome for them rather than a missing case.
const INDICATOR_BY_STATUS: Partial<Record<NurseTrainingStatus, IndicatorSpec>> = {
	[NurseTrainingStatus.Trainee]: { Icon: School, labelKey: "tooltip.nurse.trainee", testId: "nurse-status-trainee-icon" },
	[NurseTrainingStatus.Orientation]: { Icon: MenuBook, labelKey: "tooltip.nurse.orientation", testId: "nurse-status-orientation-icon" },
	[NurseTrainingStatus.Education]: { Icon: CastForEducation, labelKey: "tooltip.nurse.education", testId: "nurse-status-education-icon" },
};

type Props = {
	trainingStatus: NurseTrainingStatus | undefined;
	/** Sizing/colour for the icon, so each surface can match its own accent (e.g. a shift card's tint). */
	sx?: SxProps<Theme>;
	/** For hosts that already wrap the icon in their own tooltip, so the two open on different sides. */
	tooltipPlacement?: TooltipProps["placement"];
};

/** Icon + tooltip marking a shift worked in a training capacity (Trainee / Orientation / Education). */
export const TrainingStatusIndicator: FC<Props> = ({ trainingStatus, sx, tooltipPlacement }) => {
	const { t } = useTranslation();
	if (!trainingStatus) return null;
	const spec = INDICATOR_BY_STATUS[trainingStatus];
	if (!spec) return null;
	const { Icon, labelKey, testId } = spec;
	const label = t(labelKey);
	return (
		<InHouseTooltip title={label} placement={tooltipPlacement}>
			<Icon aria-label={label} data-testid={testId} sx={sx} />
		</InHouseTooltip>
	);
};
